import { useEffect, useState } from "react";
import { Users } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { useUserDirectory } from "@/lib/userDirectory";
import UserAvatar from "@/components/ui/UserAvatar";

const ROLE = { admin: "Admin", manager: "Manager", user: "Utilisateur" };
const name = (u) => `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email;

function Av({ u, size = 22, style }) {
  const { byId } = useUserDirectory();
  return (
    <span className="inline-flex rounded-full" style={{ width: size, height: size, ...style }}>
      <UserAvatar user={byId[u.id] || u} size={size} showPopover={false} />
    </span>
  );
}

/** Ligne « Destinataires » d'une notification système + panneau de sélection (Premier Admin). */
export default function RecipientsLine({ notifKey, label, ids = [], users = [], editable, onSaved }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(ids);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSel(ids); }, [ids]); // eslint-disable-line react-hooks/exhaustive-deps
  const active = users.filter((u) => u.status === "active" && !u.is_deleted);
  const chosen = ids.map((i) => users.find((u) => u.id === i)).filter(Boolean);
  const shown = chosen.slice(0, 5), extra = chosen.length - shown.length;
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => {
    setBusy(true);
    try { await api.put(`/settings/system-recipients/${notifKey}`, { user_ids: sel }); toast.success("Destinataires enregistrés"); setOpen(false); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  return (
    <div className="relative mt-2 flex items-center" style={{ gap: 10 }} data-testid={`email-pref-recipients-${notifKey}`}>
      <span style={{ fontSize: 11, color: "#8b8b93" }}>Destinataires</span>
      <span className="flex items-center" data-testid={`email-pref-recipients-avatars-${notifKey}`}>
        {shown.map((u, i) => <Av key={u.id} u={u} style={{ marginLeft: i ? -6 : 0, border: "1.5px solid #141418", position: "relative", zIndex: 10 - i }} />)}
        {extra > 0 && <span className="inline-flex items-center justify-center rounded-full" style={{ width: 22, height: 22, marginLeft: -6, background: "#2a2a30", color: "#8b8b93", fontSize: 8.5, fontWeight: 700, border: "1.5px solid #141418" }}>+{extra}</span>}
      </span>
      {editable && (
        <button type="button" onClick={() => { setSel(ids); setOpen((o) => !o); }} className="inline-flex items-center" style={{ gap: 5, fontSize: 11, padding: "3px 10px", border: "0.5px solid #3a3a40", borderRadius: 6, background: "transparent", color: "#c9c9d1" }} data-testid={`email-pref-recipients-edit-${notifKey}`}><Users size={13} />Modifier</button>
      )}
      {open && (
        <div className="absolute left-0 top-full mt-2" style={{ zIndex: 30, width: 360, background: "#18181c", border: "0.5px solid #3a3a40", borderRadius: 10, padding: 14, boxShadow: "0 12px 32px rgba(0,0,0,.5)" }} data-testid={`email-pref-recipients-panel-${notifKey}`}>
          <div style={{ fontSize: 12, fontWeight: 500, color: "#e8e8ec" }}>Destinataires — {label}</div>
          <div style={{ fontSize: 11, color: "#8b8b93", marginBottom: 8 }}>Seuls les utilisateurs cochés recevront cette notification.</div>
          <div style={{ maxHeight: 280, overflowY: "auto" }}>
            {active.map((u, i) => (
              <label key={u.id} className="flex items-center cursor-pointer" style={{ gap: 10, padding: "7px 0", borderTop: i ? "0.5px solid #26262a" : "none" }} data-testid={`email-pref-recipient-opt-${notifKey}-${u.id}`}>
                <Av u={u} />
                <span style={{ flex: 1, minWidth: 0 }}><span className="block truncate" style={{ fontSize: 12, color: "#e8e8ec" }}>{name(u)}</span><span style={{ fontSize: 10, color: "#8b8b93" }}>{u.is_first_admin ? "Premier Admin" : ROLE[u.role] || u.role}</span></span>
                <input type="checkbox" checked={sel.includes(u.id)} onChange={() => toggle(u.id)} style={{ width: 16, height: 16, accentColor: "#3568B0" }} />
              </label>
            ))}
          </div>
          <div className="flex justify-end items-center" style={{ gap: 12, marginTop: 10 }}>
            <button type="button" onClick={() => setOpen(false)} style={{ fontSize: 12, color: "#8b8b93", background: "transparent", border: "none" }} data-testid={`email-pref-recipients-cancel-${notifKey}`}>Annuler</button>
            <button type="button" disabled={!sel.length || busy} onClick={save} style={{ fontSize: 12, padding: "6px 14px", borderRadius: 7, background: "#3568B0", color: "#fff", opacity: !sel.length || busy ? .4 : 1 }} data-testid={`email-pref-recipients-save-${notifKey}`}>Enregistrer</button>
          </div>
        </div>
      )}
    </div>
  );
}
